<template>
  <div
    ref="app"
    id="app"
    :class="[ {'u-cursor-grab': $route.name.includes('city') || $route.name.includes('map')}, {'is-grabbing': isPointerdown}, isDistrictHover !== -1 ? 'u-cursor-pointer' : '', 'is-page-' + $route.name ]"
  >
    <SymbolsSvg />
    <div ref="containerWebGL" class="u-wrapper-panel"/>
    <div ref="containerCSS3D" class="u-wrapper-panel u-overflow-hidden u-pointer-none"/>
    <transition name="t-fader">
      <router-view
        v-if="isLoaded && isAssetsLoaded"
        :cameraPos="cameraPos"
        class="u-wrapper-panel"
        @onPrev="onPrevDistrict"
        @onNext="onNextDistrict"
        :isTuto="isBoarding"
      />
    </transition>
    <app-town @loaded="onLoaded" />
    <app-district
      v-for="(district, index) in districts"
      :data="district"
      :key="index"
      :index="index"
      :cameraPos="cameraPos"
      :isActive="isDistrictHover === index"
      @hover="onDistrictHover(index)"
      @out="onDistrictOut(index)"
      @position="onPositionDistrict"
      @click="$router.push({...$local('district'), params: {slug: district.slug }})"
      @loaded="onLoaded"
      :isFocused="index === activeItem"
    />
    <position-helper />

    <transition name="t-fader">
      <template v-if="$route.meta.isMenu">
        <span>
          <app-dropdown />
          <app-menu />
        </span>
      </template>
    </transition>
    <transition name="t-fader">
      <on-boarding v-if="isBoarding" @onBoardingFinished="isBoarding = false" />
    </transition>
    <transition name="t-fader">
      <img v-if="isDemo" class="quit-demo u-block u-cursor-pointer u-z-middle" @click.stop="$hub.emit('showoff:end')" src="/images/close.png">
    </transition>
    <transition name="t-fader">
      <loader v-if="contentLoaded !== contentToLoad" :load="contentLoaded/contentToLoad" />
    </transition>
    <intercom />
  </div>
</template>

<script>
import App from './App'
import { webGL } from '@/webGL/WebGL'
import { EffectObject } from '@/webGL/EffectObject'

import {
  AmbientLight,
  PointLight,
  AxesHelper,
  DirectionalLight,
  Object3D,
  Vector3
} from 'three'
import gsap from 'gsap'

import SymbolsSvg from '@/components/SymbolsSvg'
import PositionHelper from '@/components/common/PositionHelper'
import AppMenu from '@/components/common/AppMenu'
import AppDropdown from '@/components/common/AppDropdown'
import OnBoarding from '@/components/OnBoarding'
import Intercom from '@/components/common/Intercom'

import AppDistrict from '@/components/3d/AppDistrict'
import AppTown from '@/components/3d/AppTown'

import Loader from '@/views/Loader'

import { mapper } from '@/utils/Maths'
import { auth } from '@/utils/Auth'


export default {
  name: 'App',

  mixins: [App],

  components: {
    AppDistrict,
    AppTown,
    PositionHelper,
    AppMenu,
    AppDropdown,
    SymbolsSvg,
    Loader,
    OnBoarding,
    Intercom
  },

  data () {
    return {
      isDistrictHover: -1,
      cameraPos: {x: 0, y:0, z:0},
      subCameraPos: new Vector3(),
      subCameraLook: new Vector3(),
      isAssetsLoaded: false,
      activeItem: -1,
      isDemo: false,
      loaded: 0,
      isBoarding: false,
      isPointerdown: false
    }
  },

  computed: {
    districts () {
      return this.$store.getters['data/districts']
    },
    isLoaded () {
      return this.$store.getters['data/isLoaded']
    },
    isNameVisible () {
      return this.$route.name.includes('city')
    },
    contentToLoad () {
      return this.$store.getters['data/districts'].length + 1 + 1 // add town + add data loading
    },
    contentLoaded () {
      let loaded = this.loaded
      loaded += this.isLoaded ? 1 : 0
      return loaded
    }
  },

  async created () {
    // When a request crash of 403
    this.$hub.on('TOKEN:NEEDSREFRESH', () => {
      // If route is protected, redirect to login
      if (this.$route.meta.needsCredential) {
        this.$router.push(this.$local('login'))
      }
    })

    // Check token
    await auth.handleAuth().then(res => {
      this.$hub.emit('TOKEN', res)
    }).catch(() => {
      if (this.$route.meta.needsCredential) {
        this.$router.push(this.$local('login'))
      }
    })
    this.$store.dispatch('user/user').then(() => {
      // if first ogin, redirect to account settings
      if (this.$store.getters['user/user'].firstLogin) {
        this.isBoarding = true
        this.$router.push(this.$local('account'))
      }
    })

    this.$store.dispatch('data/global')

    const ambient = new AmbientLight(0xffffff, 1)
    const directionalLight = new DirectionalLight( 0xffffff, 3)
    const helper = new AxesHelper(1)

    directionalLight.position.set(10, 10, 10)
    const target = new Object3D()
    webGL.scene.add(target)
    directionalLight.target = target
    directionalLight.castShadow = true
    directionalLight.shadow.camera.bottom = -50
    directionalLight.shadow.camera.top = 50
    directionalLight.shadow.camera.left = -50
    directionalLight.shadow.camera.right = 50
    directionalLight.shadow.mapSize.width = 2048
    directionalLight.shadow.mapSize.height = 2048
    directionalLight.shadow.bias = -0.0006

    webGL.camera.position.copy(this.$store.getters['global/introCameraPos'])
    webGL.camera.look.copy(this.$store.getters['global/introCameraLook'])

    // webGL.scene.add(helper)

    this.positionsDistrict = {}
    this.isCameraMoving = false
    this.effectObject = new EffectObject(webGL.scene, webGL.renderer.webGLRenderer)
    this.timeoutKeypoint = null
    this.timeoutDistrict = null

    this.$hub.on('cameraMove', value => {
      this.isCameraMoving = value
    })
  },

  mounted () {
    this.$store.commit('global/webGLContainer', this.$refs.containerWebGL)
    this.$hub.on('showoff:start', this.onDemo)
    this.$hub.on('showoff:end', this.onDemoEnd)

    this.$hub.on('mainScene:pause', webGL.pause)
    this.$hub.on('mainScene:start', webGL.start)

    this.setMainScene(this.$route)
    this.setCameraPos(this.$route, true)

    this.$el.addEventListener(this.$device.pointerdown, this.onPointerdown)
    this.$el.addEventListener(this.$device.pointerup, this.onPointerup)
    this.$el.addEventListener(this.$device.pointermove, this.onPointermove)
  },

  methods: {
    onUpdate (args) {
      if (this.mouseEvent) {
        this.$device.setRaycast(this.mouseEvent, webGL.camera)
      }

      this.cameraPos.x = webGL.camera.position.x
      this.cameraPos.y = webGL.camera.position.y
      this.cameraPos.z = webGL.camera.position.z

      if (this.$store.getters['global/isIntroducted']) {
        // webGL.renderer.bokeh = mapper(webGL.camera.position.y, this.$store.getters['global/lvlScroll'].min, this.$store.getters['global/lvlScroll'].max, 1, 5)

        // Dirty fix cause camera position is sometimes to NaN (seems to be caused by GSAP)
        // webGL.renderer.bokeh = isNaN(webGL.renderer.bokeh) ? 1 : webGL.renderer.bokeh
      }

      this._onUpdate(args)
    },
    onDistrictHover (index) {
      this.isDistrictHover = index
    },
    onDistrictOut (index) {
      this.isDistrictHover = this.isDistrictHover === index ? -1 : this.isDistrictHover
    },
    onPositionDistrict (args) {
      const index = args.index
      delete args.index
      this.positionsDistrict[index] = args
    },
    onPointermove (event) {
      this.mouseEvent = event
    },
    onPointerdown (event) {
      this.mouseEvent = event
      this.isPointerdown = true

      if (this.isDemo) {
        this.$hub.emit('showoff:end')
      }
    },
    onPointerup () {
      this.isPointerdown = false
      if (this.isCameraMoving) {return}

      if (this.isDistrictHover !== -1) {
        const selectedItem = this.$store.getters['data/districtByIndex'](this.isDistrictHover)
        this.$ga.send('District', 'Click', selectedItem.slug)
        this.$router.push({...this.$local('district'), params: {slug: selectedItem.slug }})
        this.activeItem = this.isDistrictHover + 1
      }

      this.isDistrictHover = -1
    },
    onLoaded () {
      this.loaded++

      if (this.loaded === 1 + this.districts.length) {
        this.$hub.emit('3d:loaded')
        this.effectObject.fullLoaded()
        this.isAssetsLoaded = true
        this.setActiveItem(this.$route)
      }
    },
    onPrevDistrict (forceIndex) {
      const districts = this.$store.getters['data/districts']
      const activeDistricIndex = forceIndex ? forceIndex : this.districts.findIndex(district => { return district.slug === this.$route.params.slug })

      if(districts[activeDistricIndex - 1] && districts[activeDistricIndex - 1].visible === 'true') {
        this.$router.push({...this.$local('district'), params: {slug: districts[activeDistricIndex - 1].slug }})
      }
      else if (districts[districts.length - 1].visible === 'true') {
        this.$router.push({...this.$local('district'), params: {slug: districts[districts.length - 1].slug }})
      }
      else {
        this.onPrevDistrict(districts.length - 1)
      }
    },
    onNextDistrict (forceIndex) {
      const districts = this.$store.getters['data/districts']
      const activeDistricIndex = forceIndex ? forceIndex : this.districts.findIndex(district => { return district.slug === this.$route.params.slug })

      if(districts[activeDistricIndex + 1] && districts[activeDistricIndex + 1].visible === 'true') {
        this.$router.push({...this.$local('district'), params: {slug: districts[activeDistricIndex + 1].slug }})
      }
      else if (districts[0].visible === 'true') {
        this.$router.push({...this.$local('district'), params: {slug: districts[0].slug }})
      }
      else {
        this.onPrevDistrict(1)
      }
    },

    setActiveItem (to) {
      if (to.name.includes('city')) {
        this.activeItem = -1
      }
      else if (to.name.includes('district')) {
        this.activeItem = this.$store.getters['data/districtIndexBySlug'](this.$route.params.slug)
      }
    },

    onDemo (index = 0) {
      this.isDemo = true

      if (index < 4) {
        const district = this.districts[index + 1]

        if (district.visible !== 'true') {
          this.onDemo(index + 1)
          return
        }

        this.$router.push({...this.$local('district'), params: {slug: district.slug }})
        this.timeoutKeypoint = setTimeout(()=>{
          this.$hub.emit('showoff:selectRandom')
        }, 4100)
      }
      else {
        this.$router.push({...this.$local('city')})
        this.onDemoEnd()
        return
      }

      this.timeoutDistrict = setTimeout(() =>{
        this.onDemo(index + 1)
      }, 8000)
    },

    onDemoEnd () {
      clearTimeout(this.timeoutDistrict)
      clearTimeout(this.timeoutKeypoint)
      this.isDemo = false
      this.$router.push({...this.$local('city')})
    },

    setMainScene (to) {
      if (!to.meta.isMainScene) {
        this.$hub.emit('mainScene:pause')
      }
      else {
        this.$hub.emit('mainScene:start')
      }
    },

    setCameraPos (to, force = false) {
      if (to.meta.cameraBase) {
        const pos = this.$store.getters['global/introCameraPos']
        const look = this.$store.getters['global/introCameraLook']

        if (!force) {
          gsap.killTweensOf(webGL.camera.position, 'x,y,z')
          gsap.killTweensOf(webGL.camera.look, 'x,y,z')
          gsap.killTweensOf(webGL.renderer, 'bokeh')

          gsap.to(webGL.camera.position, {
            x: pos.x,
            y: pos.y,
            z: pos.z,
            duration: 3,
            ease: 'power4.inOut'
          })

          gsap.to(webGL.camera.look, {
            x: look.x,
            y: look.y,
            z: look.z,
            duration: 3,
            ease: 'power4.inOut'
          })

          gsap.to(webGL.renderer, {
            bokeh: 0,
            duration: 2,
            ease: 'linear'
          })
        }
        else {
          webGL.camera.position.copy(pos)
          webGL.camera.look.copy(look)
          webGL.renderer.bokeh = 0
        }
      }
      else {
        if (!force) {
          gsap.to(webGL.renderer, {
            bokeh: 1,
            duration: 2,
            ease: 'linear',
            onComplete: () =>{
              this.$store.commit('global/introducted', true)
            }
          })
        }
        else {
          webGL.renderer.bokeh = 1
        }
      }
    }
  },

  watch: {
    $route (to, from) {
      this.setActiveItem(to)
      this.setMainScene(to)
      this.setCameraPos(to)
    }
  }
}
</script>

<style lang="stylus" src="./styles/theme.styl"/>

<style lang="stylus">
@import '~@/styles/settings/variables'

#app
  position fixed
  top .5px
  bottom .5px
  left 0
  right 0
  overflow hidden

.logo
  position absolute
  top: $spacings.md
  left: 125px
  width 150px

.quit-demo
  width 80px
  position absolute
  bottom: $spacings.md
  left calc( 50% - 40px )
</style>

<!-- <style lang="stylus">
@import '~@/styles/settings/variables'
</style> -->
